/**
 * Risk-of-bias contract (F17, post-launch phase 1): the RoB queue, a study's
 * assessments per outcome with AI-drafted answers and their quotes, saving
 * answers and domain judgements, sign-off, reopening and draft requests.
 * Re-exported from index.ts.
 *
 * Owner: rob (F17). Tools are data (RBA-017): tool, domain, question,
 * answer and judgement values are strings defined by the tool in
 * `@bioterafi/evidence-model` (robTools.ts); the web app reads the tool's
 * definition from there. Endpoints E150 to E155 in specs/architecture/api.md.
 */
import type { StudyDesign, ValueStatus } from '@bioterafi/evidence-model';
import type { DocumentResponse, ReviewSpan } from './extraction';
import { pagePath } from './paths';

const studyRob = (slug: string, studyId: string) => `${pagePath(slug)}/studies/${encodeURIComponent(studyId)}/rob`;

export const ROB_ROUTES = {
  /** GET -> 200 RobQueueResponse (E150; RBA-083). */
  robQueue: (slug: string) => `${pagePath(slug)}/rob/queue`,
  /** GET -> 200 StudyRobResponse (E151; RBA-082). */
  studyRob,
  /** PUT RobAssessmentInput -> 200 RobAssessmentDetail (E152; RBA-081). */
  robAssessment: (slug: string, studyId: string, outcomeId: string) => `${studyRob(slug, studyId)}/${encodeURIComponent(outcomeId)}`,
  /** POST RobSignOffInput -> 200 RobAssessmentDetail (E153; RBA-055, RBA-086). */
  robSignOff: (slug: string, studyId: string, outcomeId: string) => `${studyRob(slug, studyId)}/${encodeURIComponent(outcomeId)}/sign-off`,
  /** POST RobReopenInput -> 200 RobAssessmentDetail, owner (E154; RBA-059). */
  robReopen: (slug: string, studyId: string, outcomeId: string) => `${studyRob(slug, studyId)}/${encodeURIComponent(outcomeId)}/reopen`,
  /** POST RobDraftRequest -> 202 RobDraftResponse (E155; RBA-080). */
  robDraft: (slug: string, studyId: string) => `${studyRob(slug, studyId)}/draft`,
} as const;

export type RobDraftState = 'none' | 'queued' | 'drafted' | 'failed';
export type RobDirection = 'favours_experimental' | 'favours_comparator' | 'towards_null' | 'away_from_null' | 'unpredictable';
export type RobVariant = 'parallel' | 'cluster_with_parallel_tool' | 'crossover_with_parallel_tool';

/** One signalling question's answer. Every question of the tool is listed; the web app works out which apply. */
export interface RobAnswerOut {
  questionId: string;
  /** Null: unanswered. 'NA': the question does not apply. */
  answer: string | null;
  supportText: string;
  /** Verified quotes from the study's documents (RBA-043). */
  spans: ReviewSpan[];
  /** The AI's verified answer, or null (RBA-052). */
  modelAnswer: string | null;
  /** 'model:anthropic/<id>' or 'user:<id>'; server data, never shown as a model name (CONT-058). Null when unanswered. */
  actor: string | null;
  status: ValueStatus | null;
  /** RBA-024: an AI answer whose quotes could not be found in the documents; shown, never used. */
  unverifiedProposal: { answer: string; supportText: string } | null;
  /** RBA-047: a redraft that differs from a person's answer. */
  redraftProposal: { answer: string; supportText: string } | null;
}

export interface RobDomainOut {
  domain: string;
  /** True for a domain shared by every outcome of the study (RBA-012). */
  studyLevel: boolean;
  /** The tool's algorithm on the current answers; null while a question is missing (RBA-038). */
  algorithm: string | null;
  missing: string[];
  /** The AI's own judgement and rationale (RBA-044). */
  model: { judgement: string; rationale: string } | null;
  /** The AI's judgement differs from the algorithm on the AI's own answers (RBA-044). */
  modelDisagreesWithAlgorithm: boolean;
  /** The reviewer's judgement; null until reviewed. */
  final: string | null;
  rationale: string;
  overridden: boolean;
  overrideRationale: string | null;
  direction: RobDirection | null;
  /** Null until reviewed or drafted. */
  status: ValueStatus | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
}

export interface RobAssessmentDetail {
  studyId: string;
  outcomeId: string;
  outcomeName: string;
  timepoint: string;
  /** Tool ID, e.g. 'RoB2' (RBA-017). */
  tool: string;
  toolVersion: string;
  effectOfInterest: string;
  variant: RobVariant;
  variantNote: string | null;
  answers: RobAnswerOut[];
  domains: RobDomainOut[];
  overall: {
    /** The tool's overall rule on the reviewed domain judgements; null until every domain is reviewed. */
    proposed: string | null;
    /** Set at sign-off. */
    final: string | null;
    raised: boolean;
    decisionRationale: string | null;
  };
  /** 'machine' until signed off (RBA-055). */
  status: ValueStatus;
  signedOffBy: string | null;
  signedOffAt: string | null;
  version: number;
  draft: {
    state: RobDraftState;
    /** The AI error kind when the draft failed (RBA-046). */
    errorKind: string | null;
    promptVersion: string | null;
    droppedQuotes: Array<{ questionId: string; reason: string }>;
  };
}

/**
 * Why a study has no assessment (RBA-102, RBA-104): no built tool assesses its design in this kind of
 * review (plannedTool names the published tool planned, or null), or, on a page with several eligible
 * designs, its design has not been recorded yet.
 */
export interface RobUnavailable {
  reason: 'no_tool' | 'design_not_recorded';
  design: StudyDesign;
  plannedTool: string | null;
}

export interface StudyRobResponse {
  studyId: string;
  label: string;
  design: StudyDesign;
  /** Set when no assessment can be made for the study (RBA-102, RBA-104); assessments is then empty. */
  unavailable: RobUnavailable | null;
  /** The page's primary outcome: its signed-off assessment fills the evidence table's `Study.rob` (RBA-016). */
  primaryOutcomeId: string;
  documents: DocumentResponse[];
  /** One per protocol outcome with a result for the study, in protocol order (RBA-082). */
  assessments: RobAssessmentDetail[];
  /** The next study in the queue with an assessment to sign off. */
  nextStudyId: string | null;
}

export interface RobQueueItem {
  studyId: string;
  label: string;
  outcomeId: string;
  outcomeName: string;
  /** Null when no assessment can be made (unavailable says why). */
  tool: string | null;
  unavailable: RobUnavailable | null;
  draftState: RobDraftState;
  draftError: string | null;
  domainsReviewed: number;
  domainsTotal: number;
  /** The signed-off overall judgement, or null. */
  overall: string | null;
  signedOff: boolean;
  variant: RobVariant;
}

export interface RobQueueResponse {
  /** Unsigned assessments first (RBA-083). */
  items: RobQueueItem[];
  /** Assessments with an AI draft or reviewed answers waiting for sign-off. */
  awaitingSignOff: number;
}

export interface RobAssessmentInput {
  expectedVersion: number;
  /** Answers to save; a question not listed keeps its answer. answer null clears it. */
  answers: Array<{ questionId: string; answer: string | null; supportText: string }>;
  /** Domains to mark reviewed with the reviewer's judgement (RBA-052 to RBA-054). */
  domains: Array<{ domain: string; final: string; rationale: string; overrideRationale?: string; direction?: RobDirection | null }>;
}

export interface RobSignOffInput {
  expectedVersion: number;
  /** RBA-039: required when the tool asks for a keep-or-raise decision. */
  overallDecision?: { raise: boolean; rationale: string };
  /** RBA-014: required for cluster and crossover trials. */
  variantNote?: string;
}

export interface RobReopenInput {
  expectedVersion: number;
  reason: string;
}

export interface RobDraftRequest {
  /** Empty: every protocol outcome with a result for the study. */
  outcomeIds: string[];
}

export interface RobDraftResponse {
  /** Outcome IDs whose draft waits for the next run. */
  queued: string[];
}

/** details.reason of a 409 conflict from the RoB endpoints (RBA-086). */
export type RobConflictReason = 'rob_incomplete' | 'version_conflict' | 'signed_off' | 'not_signed_off' | 'published_dataset' | 'no_tool';

/** RunWarning code of the rob stage: a draft failed (RBA-046); the run goes on. */
export type RobRunWarningCode = 'rob_draft_failed';
